import { doc, updateDoc, arrayUnion, arrayRemove, getDoc, increment } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ReactionType } from '../types/firebase';

export const initializePostReactions = () => ({
  thumbsUp: { count: 0, users: [] },
  thumbsDown: { count: 0, users: [] },
  love: { count: 0, users: [] },
  laugh: { count: 0, users: [] },
  wow: { count: 0, users: [] },
  cry: { count: 0, users: [] },
  vomit: { count: 0, users: [] },
  angry: { count: 0, users: [] },
  poop: { count: 0, users: [] },
  clown: { count: 0, users: [] }
});

export const addPostReaction = async (
  postId: string,
  userId: string,
  reactionType: ReactionType
) => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }
    
    const postData = postDoc.data();
    const currentReaction = postData.reactions?.[reactionType];
    
    // Remove user from all other reactions first
    const allReactionTypes: ReactionType[] = ['thumbsUp', 'thumbsDown', 'love', 'laugh', 'wow', 'cry', 'vomit', 'angry', 'poop', 'clown'];
    
    for (const type of allReactionTypes) {
      if (type !== reactionType && postData.reactions?.[type]?.users?.includes(userId)) {
        await updateDoc(postRef, {
          [`reactions.${type}.users`]: arrayRemove(userId),
          [`reactions.${type}.count`]: increment(-1)
        });
      }
    }
    
    // Add to the selected reaction
    if (!currentReaction?.users?.includes(userId)) {
      await updateDoc(postRef, {
        [`reactions.${reactionType}.users`]: arrayUnion(userId),
        [`reactions.${reactionType}.count`]: increment(1)
      });
    }
  } catch (error) {
    console.error('Error adding post reaction:', error);
    throw error;
  }
};

export const removePostReaction = async (
  postId: string,
  userId: string,
  reactionType: ReactionType
) => {
  try {
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      [`reactions.${reactionType}.users`]: arrayRemove(userId),
      [`reactions.${reactionType}.count`]: increment(-1)
    });
  } catch (error) {
    console.error('Error removing post reaction:', error);
    throw error;
  }
};

export const resetPostReactions = async (postId: string): Promise<void> => {
  try {
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      reactions: initializePostReactions()
    });
  } catch (error) {
    console.error('Error resetting post reactions:', error);
    throw error;
  }
};

export const cleanupPostReactions = async (postId: string): Promise<void> => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      return;
    }
    
    const postData = postDoc.data();
    const reactions = postData.reactions || {};
    
    // Clean up reactions with zero count
    const cleanedReactions = { ...reactions };
    Object.keys(cleanedReactions).forEach(key => {
      const reaction = cleanedReactions[key as ReactionType];
      if (reaction.count === 0) {
        delete cleanedReactions[key as ReactionType];
      }
    });
    
    await updateDoc(postRef, {
      reactions: cleanedReactions
    });
  } catch (error) {
    console.error('Error cleaning up post reactions:', error);
    throw error;
  }
};

export const cleanupAllPostReactions = async (): Promise<void> => {
  try {
    // This would require a batch operation to clean up all posts
    // For now, we'll just log that this function was called
    console.log('cleanupAllPostReactions called - would clean up all post reactions');
  } catch (error) {
    console.error('Error cleaning up all post reactions:', error);
    throw error;
  }
};

export const getPostReactionCounts = async (postId: string): Promise<{ [key in ReactionType]: number }> => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      return {
        thumbsUp: 0, thumbsDown: 0, love: 0, laugh: 0, wow: 0,
        cry: 0, vomit: 0, angry: 0, poop: 0, clown: 0
      };
    }
    
    const postData = postDoc.data();
    const reactions = postData.reactions || {};
    
    return {
      thumbsUp: reactions.thumbsUp?.count || 0,
      thumbsDown: reactions.thumbsDown?.count || 0,
      love: reactions.love?.count || 0,
      laugh: reactions.laugh?.count || 0,
      wow: reactions.wow?.count || 0,
      cry: reactions.cry?.count || 0,
      vomit: reactions.vomit?.count || 0,
      angry: reactions.angry?.count || 0,
      poop: reactions.poop?.count || 0,
      clown: reactions.clown?.count || 0
    };
  } catch (error) {
    console.error('Error getting post reaction counts:', error);
    throw error;
  }
};

export const getUserPostReactions = async (postId: string, userId: string): Promise<ReactionType[]> => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      return [];
    }
    
    const postData = postDoc.data();
    const reactions = postData.reactions || {};
    
    const userReactions: ReactionType[] = [];
    Object.entries(reactions).forEach(([type, reaction]) => {
      if ((reaction as any)?.users?.includes(userId)) {
        userReactions.push(type as ReactionType);
      }
    });
    
    return userReactions;
  } catch (error) {
    console.error('Error getting user post reactions:', error);
    throw error;
  }
}; 